import { Box, Flex, Stack } from '@chakra-ui/react';
import React from 'react';

import { Icon, Label, Paragraph, RadioPseudo } from 'DesignLibrary/atoms';
import Text from 'Shared/Text';
import { LocationProps } from 'Types/entities';

import { LocationLabel } from './styled';

interface ModalLocationProps {
  location: LocationProps;
  setSelectedLocation: (location: LocationProps) => void;
  isSelected: boolean;
  uniqueId: number;
  setSelectedIndex: (selectedIndex: number) => void;
  showAcceptingNewPatients: boolean;
  shouldShowGroupName: boolean;
}

export const Location = ({
  location, // Object representing the location
  setSelectedLocation, // Function to set location of provider
  isSelected, // Flag to check location is selected
  uniqueId,
  setSelectedIndex,
  showAcceptingNewPatients,
  shouldShowGroupName,
}: ModalLocationProps) => {
  const { address, distance, phoneNumbers, group_name } = location;
  // Get primary phone number
  const number = phoneNumbers?.find(
    (phoneNumber: { details: string }) => phoneNumber.details === 'primary',
  )?.phone;

  // Handler to enable add button and add selected location
  const handleOnClick = () => {
    setSelectedIndex(uniqueId);
    setSelectedLocation(location);
  };

  return (
    <LocationLabel data-testid="location">
      <input
        type="radio"
        name="location"
        className="inputStyle"
        checked={isSelected}
        onClick={handleOnClick}
        data-testid="location-radio-input"
      />

      <Flex
        className="pseudo-radio"
        align="flex-start"
        gap={4}
        p={4}
        borderRadius="var(--border-radius-xs)"
        bg="var(--background-gray-light)"
        border={`2px solid ${isSelected ? 'var(--primary-blue)' : 'transparent'}`}
      >
        <Box pointerEvents="none">
          <RadioPseudo
            sizing="small"
            color="--primary-blue"
            isChecked={isSelected}
            uniqueId={`radioGroupId-${uniqueId}`}
          />
        </Box>
        <Stack>
          {shouldShowGroupName && group_name && (
            <Paragraph
              weight="bold"
              color="--text-gray"
              className="provider-location-group-name"
              data-testid="selected-doctor-group-name"
              size="mini"
            >
              {group_name}
            </Paragraph>
          )}
          {location.name && (
            <Paragraph
              weight="bold"
              color="--text-black"
              className="provider-location-name"
              data-testid="selected-doctor-name"
              size="small"
            >
              {location.name}
            </Paragraph>
          )}
          <Paragraph size="small" data-testid="address">
            {address}
          </Paragraph>

          <Flex flexDir={['column', 'row']} gap={4}>
            <Flex align="center" gap={1}>
              <Icon type="MapPin" size="small" weight="fill" />
              <Paragraph
                size="mini"
                color="--text-black"
                weight="bold"
                className="distance"
                data-testid="distance"
              >
                {`${distance} miles`}
              </Paragraph>
            </Flex>
            {number && (
              <Paragraph size="mini" data-testid="number">
                {`(${number.slice(0, 3)}) 
          ${number.slice(3, 6)}-${number.slice(6)}`}
              </Paragraph>
            )}
          </Flex>
          {showAcceptingNewPatients && location.isAcceptingPatients && (
            <Flex align="center" gap={1} className="tag">
              <Icon type="CheckCircle" size="small" />
              <Label
                weight="normal"
                color="--text-black"
                className="accepting-new-patients"
                data-testid="accepting-new-patients-label"
              >
                <Text field="health_section.secondary_content.provider_selection_accepting_new_patients" />
              </Label>
            </Flex>
          )}
        </Stack>
      </Flex>
    </LocationLabel>
  );
};
